import React from 'react';
import { LocationData } from '../MerchantHeatmap.data';
import MerchantHeatMap from './map/MerchantHeatMap';
import { LiveEventActivityLevelsPanel, LiveEventAreaItem } from './LiveEventActivityLevelsPanel';

interface AttendeeGeographicDistributionMapProps {
    /** Locations for the heat map (same shape as coupon heatmap; redemptions used for density coloring) */
    locations: LocationData[];
    totalAttendees: number;
    /** Items for the Activity Levels panel (attendees, most popular event, demographics) */
    panelItems: LiveEventAreaItem[];
    selectedLocation?: LocationData | null;
    onLocationSelect?: (location: LocationData | null) => void;
}

/**
 * Attendee Geographic Distribution: Customer Map + Attendee Density legend + Total Attendees/Areas + Activity Levels panel.
 * Figma: node-id=21203-2626
 */
export const AttendeeGeographicDistributionMap: React.FC<AttendeeGeographicDistributionMapProps> = ({
    locations,
    totalAttendees,
    panelItems,
    selectedLocation = null,
    onLocationSelect,
}) => {
    return (
        <div className="w-full flex flex-col xl:flex-row gap-[16px] items-stretch">
            {/* Map card */}
            <div className="w-full xl:flex-[2] bg-white dark:bg-gray-800 rounded-[16px] outline outline-1 outline-[#E5E7EB] dark:outline-gray-700 outline-offset-[-1px] p-[0.80px] xl:h-[554px] flex flex-col">
                <div className="p-[20px] flex flex-col gap-[15px] shrink-0">
                    <div className="flex flex-col gap-[4px]">
                        <div className="text-[#111827] dark:text-white text-[15px] font-normal leading-[20px]" style={{ fontFamily: 'Nunito' }}>
                            Customer Map
                        </div>
                    </div>
                </div>

                <div className="px-[16px] pb-[14px] flex-1 min-h-0 relative flex flex-col justify-center items-stretch">
                    <div className="relative w-full h-full min-h-[478.40px] bg-[#DDDDDD] dark:bg-gray-700 rounded-[16px] overflow-hidden">
                        <MerchantHeatMap
                            locations={locations}
                            selectedLocation={selectedLocation ?? null}
                            onLocationSelect={onLocationSelect ?? (() => {})}
                            borderRadiusPx={16}
                        />

                        {/* Attendee Density legend (top-right) */}
                        <div
                            className="absolute z-[9999] bg-white dark:bg-gray-900 rounded-[12px] outline outline-1 outline-[#E5E7EB] dark:outline-gray-700 outline-offset-[-1px]"
                            style={{
                                right: '24px',
                                top: '24px',
                                paddingLeft: '15.8px',
                                paddingRight: '15.8px',
                                paddingTop: '11.8px',
                                paddingBottom: '11.8px',
                                boxShadow: '0px 4px 6px -1px rgba(0,0,0,0.10)',
                            }}
                        >
                            <div className="text-[#111827] dark:text-white text-[12px] font-normal leading-[16px]" style={{ fontFamily: 'Nunito' }}>
                                Attendee Density
                            </div>
                            <div className="mt-[8px] flex flex-col gap-[8px]">
                                <div className="flex items-center gap-[8px]">
                                    <div className="w-[20px] h-[12px] rounded-[4px] bg-[#EF4444]" />
                                    <div className="text-[#4B5563] dark:text-gray-300 text-[11px] font-normal leading-[14px]" style={{ fontFamily: 'Nunito' }}>
                                        High (60+)
                                    </div>
                                </div>
                                <div className="flex items-center gap-[8px]">
                                    <div className="w-[20px] h-[12px] rounded-[4px] bg-[#F59E0B]" />
                                    <div className="text-[#4B5563] dark:text-gray-300 text-[11px] font-normal leading-[14px]" style={{ fontFamily: 'Nunito' }}>
                                        Medium (30-59)
                                    </div>
                                </div>
                                <div className="flex items-center gap-[8px]">
                                    <div className="w-[20px] h-[12px] rounded-[4px] bg-[#5EC269]" />
                                    <div className="text-[#4B5563] dark:text-gray-300 text-[11px] font-normal leading-[14px]" style={{ fontFamily: 'Nunito' }}>
                                        Low (&lt;30)
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Total Attendees / Areas Covered (bottom-left) */}
                        <div
                            className="absolute z-[9999] bg-white dark:bg-gray-900 rounded-[12px] outline outline-1 outline-[#E5E7EB] dark:outline-gray-700 outline-offset-[-1px]"
                            style={{
                                left: '24px',
                                bottom: '24px',
                                paddingLeft: '15.8px',
                                paddingRight: '15.8px',
                                paddingTop: '11.8px',
                                paddingBottom: '11.8px',
                                boxShadow: '0px 4px 6px -1px rgba(0,0,0,0.10)',
                            }}
                        >
                            <div className="flex items-center gap-[24px]">
                                <div className="flex flex-col gap-[4px]">
                                    <div className="text-[#4B5563] dark:text-gray-400 text-[11px] font-normal leading-[14px]" style={{ fontFamily: 'Nunito' }}>
                                        Total Attendees
                                    </div>
                                    <div className="text-[#111827] dark:text-white text-[18px] font-normal leading-[22px]" style={{ fontFamily: 'Nunito' }}>
                                        {totalAttendees}
                                    </div>
                                </div>
                                <div className="w-[1px] h-[32px] bg-[#E5E7EB] dark:bg-gray-700" />
                                <div className="flex flex-col gap-[4px]">
                                    <div className="text-[#4B5563] dark:text-gray-400 text-[11px] font-normal leading-[14px]" style={{ fontFamily: 'Nunito' }}>
                                        Areas Covered
                                    </div>
                                    <div className="text-[#111827] dark:text-white text-[18px] font-normal leading-[22px]" style={{ fontFamily: 'Nunito' }}>
                                        {Math.max(1, locations.length)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Activity Levels panel */}
            <div className="w-full xl:w-[304px] xl:h-[554px] shrink-0">
                <LiveEventActivityLevelsPanel items={panelItems} showPrimaryHighlight />
            </div>
        </div>
    );
};
